import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import type { Organization } from '@/api/Organization';
import { organizationService } from '@/service/OrganizationService';
import usePersistState from './PersistState';
import WebUser from './WebUser'

// https://ibaslogic.com/routing-with-react-router/

export const AUTH_STORAGE_KEY = 'web-user';

interface AuthContextType {
  user: WebUser | null;
  login: (user: WebUser) => void;
  logout: () => void;
  update: (firstName: string, lastName: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const OrganizationContext = createContext<Organization | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // eventually inject the initial state from local storage here
  const [user, setUser] = usePersistState<WebUser | null>(AUTH_STORAGE_KEY, null);
  const [loadedOrganization, setOrganization] = useState<Organization | null>(null);
  const organizationId = user?.data.organizationId;
  const token = user?.token;
  const organization = token && loadedOrganization?.id === organizationId ? loadedOrganization : null;

  useEffect(() => {
    let cancelled = false;
    setOrganization(null);
    if (token && organizationId != null) {
      organizationService.current(token).then(value => {
        if (!cancelled && value.id === organizationId) setOrganization(value);
      }).catch(() => {
        // Keep existing branding fallbacks available if settings cannot be loaded.
      });
    }
    return () => { cancelled = true; };
  }, [token, organizationId]);
  const login = (user: WebUser) => setUser(user);
  const logout = () => setUser(null);
  const update = (firstName: string, lastName: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        data: {
          ...user.data,
          firstName,
          lastName
        }
      };
      setUser(updatedUser);
    }
  }
  return (
    <AuthContext.Provider value={{ user, login, logout, update }}>
      <OrganizationContext.Provider value={organization}>
        {children}
      </OrganizationContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null || context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export const useOrganization = () => useContext(OrganizationContext);
